/* eslint-disable react/prop-types */
/**
 * Bar loader component for the layout system.
 *
 * Calculates and displays the disk configuration for a barbell based on total
 * weight. Supports full (grid) and small (inline) rendering modes.
 * Ported from apps/electron-app/src/components/area/BarLoader.tsx into the
 * shared packages/ui layer so both Electron and Web can use it.
 */

import { Box } from "@mui/material";
import React from "react";
import { resolveStyles } from "../layout-types";
import type { ComponentNode, InjectedData } from "../layout-types";
import type { BindingExpression } from "../../../../util/src/blueprint/types";
import { useLayoutRenderScale } from "../render-scale-context";
import { getTextDecorationCssProps } from "../textDecorationCss.js";
import {
  type BarLoaderBlockStyle,
  type BarLoaderDiskDefinition,
  type BarLoaderProps,
  type BarLoaderTargetPatch,
  calculateLoadedDisks,
  buildBarLoaderDiskStylesForRender,
  getBarLoaderFootprint,
  getBarLoaderWidthDivisor,
  getEnabledDisks,
  isBarLoaderCompactVariableWidth,
  resolveBarLoaderTotalWeight,
} from "./barLoaderConfig.js";

interface LayoutBarLoaderProps {
  node: ComponentNode;
  data: InjectedData;
  bindingPatch?: BindingExpression;
  /** Ephemeral behavior patches (not persisted in layout JSON). */
  barLoaderTargetPatches?: BarLoaderTargetPatch[];
}

function textColor(bg: string): string {
  return ["#ff3b30", "#007aff", "#34c759", "#1c1c1e", "#8b0000"].includes(
    bg.toLowerCase(),
  )
    ? "white"
    : "black";
}

function scalePxInString(value: string, scale: number): string {
  return value.replace(
    /(-?\d*\.?\d+)px\b/g,
    (_, rawNumber: string) => `${Number(rawNumber) * scale}px`,
  );
}

function toCssLength(value: unknown, scale: number): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value) * scale}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return scalePxInString(value.trim(), scale);
  }
  return undefined;
}

function toLengthPx(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value.replace("px", "").trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function toSxStyle(
  style: BarLoaderBlockStyle,
  renderScale: number,
): Record<string, unknown> {
  return {
    display: style.visibility === false ? "none" : undefined,
    width: toCssLength(style.width, renderScale),
    height: toCssLength(style.height, renderScale),
    margin: toCssLength(style.margin, renderScale),
    marginTop: toCssLength(style.marginTop, renderScale),
    marginRight: toCssLength(style.marginRight, renderScale),
    marginBottom: toCssLength(style.marginBottom, renderScale),
    marginLeft: toCssLength(style.marginLeft, renderScale),
    padding: toCssLength(style.padding, renderScale),
    paddingTop: toCssLength(style.paddingTop, renderScale),
    paddingRight: toCssLength(style.paddingRight, renderScale),
    paddingBottom: toCssLength(style.paddingBottom, renderScale),
    paddingLeft: toCssLength(style.paddingLeft, renderScale),
    backgroundColor: style.backgroundColor,
    opacity: style.opacity,
    transform: style.transform,
    border:
      typeof style.border === "string"
        ? scalePxInString(style.border, renderScale)
        : style.border,
    borderTop:
      typeof style.borderTop === "string"
        ? scalePxInString(style.borderTop, renderScale)
        : style.borderTop,
    borderRight:
      typeof style.borderRight === "string"
        ? scalePxInString(style.borderRight, renderScale)
        : style.borderRight,
    borderBottom:
      typeof style.borderBottom === "string"
        ? scalePxInString(style.borderBottom, renderScale)
        : style.borderBottom,
    borderLeft:
      typeof style.borderLeft === "string"
        ? scalePxInString(style.borderLeft, renderScale)
        : style.borderLeft,
    borderRadius: toCssLength(style.borderRadius, renderScale),
    borderTopLeftRadius: toCssLength(style.borderTopLeftRadius, renderScale),
    borderTopRightRadius: toCssLength(style.borderTopRightRadius, renderScale),
    borderBottomRightRadius: toCssLength(
      style.borderBottomRightRadius,
      renderScale,
    ),
    borderBottomLeftRadius: toCssLength(
      style.borderBottomLeftRadius,
      renderScale,
    ),
    color: style.color,
    fontSize: toCssLength(style.fontSize, renderScale),
    fontFamily: style.fontFamily,
    fontWeight: style.fontWeight,
    textAlign: style.textAlign,
    textTransform: style.textTransform,
    whiteSpace: style.whiteSpace,
    ...getTextDecorationCssProps(style as Record<string, unknown>, renderScale),
  };
}

const LayoutBarLoader: React.FC<LayoutBarLoaderProps> = ({
  node,
  data,
  bindingPatch,
  barLoaderTargetPatches: barLoaderTargetPatchesProp,
}) => {
  const renderScale = useLayoutRenderScale();
  const resolved = resolveStyles(node.style, data);
  const isHidden = resolved.visibility === false;
  if (isHidden) return null;

  const props = (node.props ?? {}) as BarLoaderProps;
  const mode = props.mode ?? "full";
  const unit = props.unit ?? "kg";
  const labelOrientation = props.labelOrientation ?? "horizontal";

  const totalWeight = resolveBarLoaderTotalWeight({
    props,
    dataBinding: node.dataBinding,
    data,
    bindingPatch,
  });

  const diskDefinitions = getEnabledDisks(unit, props.optionalDisks);
  const loadedDisks = calculateLoadedDisks(
    totalWeight,
    unit,
    props.optionalDisks,
    props.barWeight,
    props.collarsWeight,
  );
  const loadedDiskItems = loadedDisks
    .map((weight) => diskDefinitions.find((disk) => disk.weight === weight))
    .filter((disk): disk is BarLoaderDiskDefinition => Boolean(disk));
  const diskStyles = buildBarLoaderDiskStylesForRender(
    props,
    barLoaderTargetPatchesProp ?? props.barLoaderTargetPatches,
  );

  const nodeWidth = Number.isFinite(node.width) ? node.width : 0;
  const nodeHeight = Number.isFinite(node.height) ? node.height : 0;

  const getLabelHeight = (disk: BarLoaderDiskDefinition): number =>
    toLengthPx(diskStyles[String(disk.weight)]?.label?.height, 56);
  const getDiskHeight = (disk: BarLoaderDiskDefinition): number =>
    toLengthPx(diskStyles[String(disk.weight)]?.disk?.height, 112);
  const getLabelWidth = (disk: BarLoaderDiskDefinition): number =>
    toLengthPx(diskStyles[String(disk.weight)]?.label?.width, 60);
  const getDiskWidth = (disk: BarLoaderDiskDefinition): number =>
    toLengthPx(diskStyles[String(disk.weight)]?.disk?.width, 60);

  const footprintDims = getBarLoaderFootprint(
    { ...props, totalWeight },
    diskStyles,
  );
  const fullModeHeightDelta = nodeHeight - footprintDims.height;
  const compactModeHeightDelta = nodeHeight - footprintDims.height;
  const labelHeightIncrement = mode === "full" ? fullModeHeightDelta / 2 : 0;
  const diskHeightIncrement =
    mode === "full" ? fullModeHeightDelta / 2 : compactModeHeightDelta;

  const widthDivisor = getBarLoaderWidthDivisor(props);
  const baseTotalWidth = footprintDims.width;
  const widthDelta = isBarLoaderCompactVariableWidth(props)
    ? 0
    : nodeWidth - baseTotalWidth;
  const widthIncrementPerItem = widthDelta / widthDivisor;

  const adjustLabelStyle = (
    disk: BarLoaderDiskDefinition,
  ): BarLoaderBlockStyle => {
    const styleEntry = diskStyles[String(disk.weight)];
    const label = styleEntry?.label ?? {};
    return {
      ...label,
      width: Math.max(0, getLabelWidth(disk) + widthIncrementPerItem),
      height: Math.max(0, getLabelHeight(disk) + labelHeightIncrement),
    };
  };

  const adjustDiskStyle = (
    disk: BarLoaderDiskDefinition,
  ): BarLoaderBlockStyle => {
    const styleEntry = diskStyles[String(disk.weight)];
    const diskStyle = styleEntry?.disk ?? {};
    return {
      ...diskStyle,
      width: Math.max(0, getDiskWidth(disk) + widthIncrementPerItem),
      height: Math.max(0, getDiskHeight(disk) + diskHeightIncrement),
    };
  };

  const rootStyle = resolveStyles(
    (node.style ?? {}) as unknown as Record<string, unknown>,
    data,
  );
  const rootSx = toSxStyle(
    rootStyle as unknown as BarLoaderBlockStyle,
    renderScale,
  );
  const nodeRotation =
    typeof node.rotation === "number" && Number.isFinite(node.rotation)
      ? node.rotation
      : 0;
  const composedTransform = [rootSx.transform, `rotate(${nodeRotation}deg)`]
    .filter(
      (value): value is string => typeof value === "string" && value.length > 0,
    )
    .join(" ")
    .trim();
  const rootWithRotationSx = {
    ...rootSx,
    transform: composedTransform.length > 0 ? composedTransform : undefined,
    transformOrigin: "center center",
  };

  const getDiskCount = (weight: number) =>
    loadedDisks.filter((w) => w === weight).length;

  const compactJustifyContent =
    props.compactDiskAlign === "start"
      ? "flex-start"
      : props.compactDiskAlign === "end"
        ? "flex-end"
        : "center";

  const renderLabel = (disk: BarLoaderDiskDefinition) => {
    const styleEntry = diskStyles[String(disk.weight)];
    const labelStyle = adjustLabelStyle(disk);
    const labelText = styleEntry?.labelText ?? String(disk.weight);
    return (
      <Box
        sx={{
          ...toSxStyle(labelStyle, renderScale),
          display: labelStyle.visibility === false ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          color: labelStyle.color ?? "#fff",
          writingMode:
            labelOrientation === "vertical" ? "vertical-rl" : undefined,
          textOrientation:
            labelOrientation === "vertical" ? "upright" : undefined,
        }}
      >
        {labelText}
      </Box>
    );
  };

  const renderDiskBlock = (
    disk: BarLoaderDiskDefinition,
    content: React.ReactNode,
  ) => {
    const diskStyle = adjustDiskStyle(disk);
    const bg = diskStyle.backgroundColor ?? disk.defaultColor;
    return (
      <Box
        sx={{
          ...toSxStyle(diskStyle, renderScale),
          display: diskStyle.visibility === false ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bg,
          color: diskStyle.color ?? textColor(bg),
          boxSizing: "border-box",
          border:
            diskStyle.border ??
            (bg.toLowerCase() === "#f5f5f5" ? "1px solid #000" : undefined),
          writingMode:
            labelOrientation === "vertical" ? "vertical-rl" : undefined,
          textOrientation:
            labelOrientation === "vertical" ? "upright" : undefined,
        }}
      >
        {content}
      </Box>
    );
  };

  /**
   * Compact modes: outer flex is full width so free space can sit left/center/right;
   * inner row is shrink-to-fit (real disks only). Placeholders would fill the row and
   * defeat justifyContent.
   */
  const compactModesOuterSx = {
    ...rootWithRotationSx,
    boxSizing: "border-box" as const,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row" as const,
    justifyContent: compactJustifyContent,
    alignItems: "center",
    overflow: "hidden",
  };

  if (mode === "small") {
    const visibleOrdered = diskDefinitions.filter(
      (d) => getDiskCount(d.weight) > 0,
    );
    return (
      <Box sx={compactModesOuterSx}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {visibleOrdered.map((disk, i) => (
            <React.Fragment key={`${disk.weight}-${i}`}>
              {renderDiskBlock(disk, getDiskCount(disk.weight))}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  }

  if (mode === "bar") {
    return (
      <Box sx={compactModesOuterSx}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            flexShrink: 0,
          }}
        >
          {loadedDiskItems.map((disk, index) => {
            const styleEntry = diskStyles[String(disk.weight)];
            return (
              <Box
                key={`${disk.weight}-${index}`}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                {renderDiskBlock(
                  disk,
                  styleEntry?.labelText ?? String(disk.weight),
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...rootWithRotationSx,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          boxSizing: "border-box",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "start",
        }}
      >
        {diskDefinitions.map((disk, i) => {
          const count = getDiskCount(disk.weight);
          return (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {renderLabel(disk)}
              {count > 0 && (
                <React.Fragment>{renderDiskBlock(disk, count)}</React.Fragment>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default LayoutBarLoader;
